import { normalizeText } from "@/shared/utils/normalizeText";
import contato from "@/tradutors/contact";
import faq from "@/tradutors/faq";
import { TOPICOS_DO_FAQ, respostaDoFaq } from "@/utils/faqRespostas";
import {
  absoluteUrl,
  defineTool,
  jsonResult,
  type WebMcpToolsContext,
} from "./shared";

/**
 * Resposta de um tópico do FAQ sem precisar estar na página de contato:
 * registrada globalmente. O texto é o mesmo que o componente do tópico mostra
 * (tradutors/faq.ts), e o endereço é o da página que resolve a dúvida.
 */
export function helpAnswerTools(ctx: WebMcpToolsContext): WebMcpToolDescriptor[] {
  const { router, language, t } = ctx;
  const titulos =
    language === "en" ? contato.ingles.labelFaq : contato.portugues.labelFaq;
  const textos = language === "en" ? faq.ingles : faq.portugues;

  return [
    defineTool(t, {
      name: "get_help_answer",
      description: t.tools.get_help_answer.description,
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: "object",
        properties: {
          topic: {
            type: "string",
            enum: titulos,
            description: t.tools.get_help_answer.params.topic,
          },
        },
        required: ["topic"],
      },
      execute: async ({ topic }) => {
        const procurado = normalizeText(String(topic ?? ""));
        const indice = titulos.findIndex(
          (titulo) => normalizeText(titulo) === procurado
        );
        if (indice < 0) return t.messages.topicNotFound;

        const { texto, destino } = respostaDoFaq(TOPICOS_DO_FAQ[indice], textos);
        return jsonResult({
          topic: titulos[indice],
          answer: texto,
          url: destino
            ? absoluteUrl(router, {
                name: destino.name,
                params: destino.semIdioma ? {} : { language },
                query: destino.query,
              })
            : null,
        });
      },
    }),
  ];
}

/**
 * Tool que abre um tópico do FAQ da página de contato.
 *
 * Existe por um motivo concreto: os painéis do accordion (`vue-collapsible
 * -panel`) só MONTAM o conteúdo quando expandidos — enquanto fechados, os
 * formulários de contato/sugestão/redefinição de senha nem estão no DOM, e as
 * tools declarativas deles (`toolname` no `<form>`) não existem para o agente.
 *
 * Com esta tool o fluxo fecha: o agente abre o tópico, o formulário monta, o
 * browser dispara `toolchange` e aí sim `contact_support` / `suggest_model` /
 * `request_password_reset` aparecem na lista. É o padrão que a própria doc do
 * WebMCP recomenda — registrar tools conforme o estado da página.
 */
export function helpTopicTools(
  ctx: WebMcpToolsContext,
  titles: string[],
  getHeaders: () => HTMLElement[]
): WebMcpToolDescriptor[] {
  const { t } = ctx;

  return [
    defineTool(t, {
      name: "open_help_topic",
      description: t.tools.open_help_topic.description,
      inputSchema: {
        type: "object",
        properties: {
          topic: {
            type: "string",
            enum: titles,
            description: t.tools.open_help_topic.params.topic,
          },
        },
        required: ["topic"],
      },
      execute: async ({ topic }) => {
        const wanted = normalizeText(String(topic ?? ""));
        const index = titles.findIndex((title) => normalizeText(title) === wanted);
        if (index < 0) return t.messages.topicNotFound;

        const header = getHeaders()[index];
        if (!header) return t.messages.error;

        // `.vcp__body` só existe quando o painel está aberto — é como sabemos se
        // já está expandido. Sem isso, um clique aqui FECHARIA o painel aberto.
        const panel = header.closest(".vcp");
        const alreadyOpen = Boolean(panel?.querySelector(".vcp__body"));
        if (!alreadyOpen) header.click();

        await nextTick();
        header.scrollIntoView({ behavior: "smooth", block: "center" });

        return `${t.messages.topicOpened} ${titles[index]}`;
      },
    }),
  ];
}
